import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import apiClient from '../utils/apiClient';
import { handleAuthError } from '../utils/authErrorHandler';
import CreditPurchaseModal from './CreditPurchaseModal';
import './CreditsMarketplace.css';

const CreditsMarketplace = ({ user, onBackToDashboard }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [usageData, setUsageData] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [purchaseHistory, setPurchaseHistory] = useState([]);

  useEffect(() => {
    loadMarketplaceData();
  }, []);

  const loadMarketplaceData = async () => {
    try {
      setLoading(true);
      
      // Charger les données d'usage
      const usageResponse = await apiClient.get('/api/usage/current');
      setUsageData(usageResponse.data);
      
      // Charger l'historique d'achats (si disponible)
      try {
        const historyResponse = await apiClient.get('/api/billing/payment-history');
        const creditPurchases = historyResponse.data.payments?.filter(
          p => p.type === 'credit_purchase'
        ) || [];
        setPurchaseHistory(creditPurchases);
      } catch (err) {
        console.log('Historique non disponible');
      }
      
    } catch (error) {
      if (handleAuthError(error)) return;
      toast.error(t('creditsMarketplace.errors.loadFailed'));
      console.error('Error loading marketplace data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseClick = (packageInfo) => {
    setSelectedPackage(packageInfo);
    setShowPurchaseModal(true);
  };

  const handlePurchaseSuccess = async (quantity) => {
    toast.success(t('creditsMarketplace.purchaseSuccess', { quantity: quantity.toLocaleString() }));
    setShowPurchaseModal(false);
    setSelectedPackage(null);
    await loadMarketplaceData();
  };

  const creditPackages = [
    {
      id: 'starter',
      quantity: 500,
      price: 2,
      pricePerCall: 0.004,
      popular: false,
      features: [
        t('creditsMarketplace.packages.starter.feature1'),
        t('creditsMarketplace.packages.starter.feature2'),
        t('creditsMarketplace.packages.starter.feature3')
      ]
    },
    {
      id: 'popular',
      quantity: 1000,
      price: 3,
      pricePerCall: 0.003,
      savings: 25,
      popular: true,
      features: [
        t('creditsMarketplace.packages.popular.feature1'),
        t('creditsMarketplace.packages.popular.feature2'),
        t('creditsMarketplace.packages.popular.feature3'),
        t('creditsMarketplace.packages.popular.feature4')
      ]
    },
    {
      id: 'premium',
      quantity: 2500,
      price: 7,
      pricePerCall: 0.0028,
      savings: 30,
      popular: false,
      features: [
        t('creditsMarketplace.packages.premium.feature1'),
        t('creditsMarketplace.packages.premium.feature2'),
        t('creditsMarketplace.packages.premium.feature3'),
        t('creditsMarketplace.packages.premium.feature4'),
        t('creditsMarketplace.packages.premium.feature5')
      ]
    }
  ];

  if (loading) {
    return (
      <div className="credits-marketplace loading">
        <div className="marketplace-loader">
          <div className="spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const currentUsage = usageData?.usage?.apiCalls || { used: 0, limit: 0, percentage: 0 };

  return (
    <div className="credits-marketplace">
      {/* Header */}
      <div className="marketplace-header">
        <button onClick={onBackToDashboard} className="back-button">
          ← {t('common.back')}
        </button>
        <div className="header-content">
          <h1>💳 {t('creditsMarketplace.title')}</h1>
          <p className="subtitle">{t('creditsMarketplace.subtitle')}</p>
        </div>
      </div>

      {/* Current Usage Banner */}
      <div className="usage-banner">
        <div className="usage-banner-content">
          <div className="usage-info">
            <div className="usage-icon">🔌</div>
            <div className="usage-details">
              <h3>{t('creditsMarketplace.currentUsage.title')}</h3>
              <div className="usage-stats">
                <span className="usage-number">
                  {currentUsage.used?.toLocaleString()} / {currentUsage.limit?.toLocaleString()}
                </span>
                <span className="usage-label">{t('creditsMarketplace.currentUsage.apiCalls')}</span>
              </div>
            </div>
          </div>
          
          <div className="usage-progress-container">
            <div className="usage-percentage-label">
              {currentUsage.percentage}% {t('creditsMarketplace.currentUsage.used')}
            </div>
            <div className="usage-progress-bar">
              <div 
                className={`usage-progress-fill ${
                  currentUsage.percentage >= 90 ? 'critical' : 
                  currentUsage.percentage >= 80 ? 'warning' : 'normal'
                }`}
                style={{ width: `${Math.min(currentUsage.percentage, 100)}%` }}
              />
            </div>
            <div className="usage-remaining">
              {currentUsage.remaining?.toLocaleString()} {t('creditsMarketplace.currentUsage.remaining')}
            </div>
          </div>
        </div>
      </div>

      {/* Credit Packages */}
      <div className="packages-section">
        <h2>{t('creditsMarketplace.packagesTitle')}</h2>
        <p className="packages-description">{t('creditsMarketplace.packagesDescription')}</p>
        
        <div className="packages-grid">
          {creditPackages.map((pkg) => (
            <div 
              key={pkg.id} 
              className={`credit-package ${pkg.popular ? 'popular' : ''}`}
            >
              {pkg.popular && (
                <div className="popular-badge">
                  ⭐ {t('creditsMarketplace.popularBadge')}
                </div>
              )}
              
              <div className="package-header">
                <h3>{pkg.quantity.toLocaleString()}</h3>
                <span className="package-unit">{t('creditsMarketplace.apiCalls')}</span>
              </div>
              
              <div className="package-price">
                <span className="price-amount">{pkg.price}€</span>
                {pkg.savings && (
                  <span className="savings-badge">
                    -{pkg.savings}%
                  </span>
                )}
              </div>
              
              <div className="price-per-call">
                {(pkg.pricePerCall * 1000).toFixed(2)}€ / {t('creditsMarketplace.per1000Calls')}
              </div>
              
              <ul className="package-features">
                {pkg.features.map((feature, index) => (
                  <li key={index}>✓ {feature}</li>
                ))}
              </ul>
              
              <button 
                className="buy-package-button"
                onClick={() => handlePurchaseClick(pkg)}
              >
                {t('creditsMarketplace.buyButton')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Info Section */}
      <div className="marketplace-info">
        <div className="info-card">
          <div className="info-icon">🎯</div>
          <h3>{t('creditsMarketplace.info.howItWorks.title')}</h3>
          <p>{t('creditsMarketplace.info.howItWorks.description')}</p>
        </div>
        
        <div className="info-card">
          <div className="info-icon">⚡</div>
          <h3>{t('creditsMarketplace.info.instantActivation.title')}</h3>
          <p>{t('creditsMarketplace.info.instantActivation.description')}</p>
        </div>
        
        <div className="info-card">
          <div className="info-icon">💰</div>
          <h3>{t('creditsMarketplace.info.noExpiration.title')}</h3>
          <p>{t('creditsMarketplace.info.noExpiration.description')}</p>
        </div>
      </div>

      {/* Purchase History */}
      {purchaseHistory.length > 0 && (
        <div className="purchase-history-section">
          <h2>{t('creditsMarketplace.purchaseHistory.title')}</h2>
          <div className="history-table">
            <table>
              <thead>
                <tr>
                  <th>{t('creditsMarketplace.purchaseHistory.date')}</th>
                  <th>{t('creditsMarketplace.purchaseHistory.quantity')}</th>
                  <th>{t('creditsMarketplace.purchaseHistory.amount')}</th>
                  <th>{t('creditsMarketplace.purchaseHistory.status')}</th>
                </tr>
              </thead>
              <tbody>
                {purchaseHistory.map((purchase, index) => (
                  <tr key={index}>
                    <td>{new Date(purchase.date).toLocaleDateString()}</td>
                    <td>{purchase.quantity?.toLocaleString()} calls</td>
                    <td>{purchase.amount}€</td>
                    <td>
                      <span className={`status-badge ${purchase.status}`}>
                        {purchase.status === 'completed' ? '✓' : '⏳'} {purchase.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <div className="faq-section">
        <h2>{t('creditsMarketplace.faq.title')}</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>{t('creditsMarketplace.faq.q1.question')}</h4>
            <p>{t('creditsMarketplace.faq.q1.answer')}</p>
          </div>
          <div className="faq-item">
            <h4>{t('creditsMarketplace.faq.q2.question')}</h4>
            <p>{t('creditsMarketplace.faq.q2.answer')}</p>
          </div>
          <div className="faq-item">
            <h4>{t('creditsMarketplace.faq.q3.question')}</h4>
            <p>{t('creditsMarketplace.faq.q3.answer')}</p>
          </div>
          <div className="faq-item">
            <h4>{t('creditsMarketplace.faq.q4.question')}</h4>
            <p>{t('creditsMarketplace.faq.q4.answer')}</p>
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      <CreditPurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => {
          setShowPurchaseModal(false);
          setSelectedPackage(null);
        }}
        currentUsage={currentUsage}
        onPurchaseSuccess={handlePurchaseSuccess}
      />
    </div>
  );
};

export default CreditsMarketplace;
